import {Component, OnDestroy} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {MatFormField, MatOption, MatSelect, MatSelectModule} from '@angular/material/select';

import {TranslatorService} from '../../services/translator.service';
import {LanguageEnum} from '../../enums/language.enum';
import {MatFormFieldModule} from '@angular/material/form-field';
import {ApplicationInfoService} from '../../services/application-info.service';
import {ApplicationInfoModel} from '../../models/application-info.model';
import {HttpClient, HttpContext} from '@angular/common/http';
import {Subject, takeUntil} from 'rxjs';
import {CollaboratorHelperService} from '../../services/collaborator-helper.service';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {SessionService} from '../../services/session.service';
import {SnackBarService} from '../../services/snackbar.service';
import {SKIP_EXCEPTION_HANDLING} from '../../tokens/skip-exception-handling.token';
import {UserHal} from '../../models/HAL/user-hal.model';

interface UserNotificationPreference {
  type: string;
  enabled: boolean;
  updating?: boolean;
}

@Component({
  selector: 'app-settings',
  imports: [
    TranslatePipe,
    MatDialogContent,
    MatDialogTitle,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatSelect,
    MatOption,
    MatFormField,
    MatFormFieldModule,
    MatSelectModule,
    MatSlideToggle
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  readonly availableLanguages = Object.keys(LanguageEnum).map(key => ({
    label: key,
    code: LanguageEnum[key as keyof typeof LanguageEnum]
  }));
  applicationInfo: ApplicationInfoModel | null = null;
  selectedLanguage: { code: LanguageEnum; label: string } = this.availableLanguages[0];
  notificationPreferences: UserNotificationPreference[] = [];
  private notificationPreferencesUrl?: string;

  constructor(private readonly translatorService: TranslatorService,
              private readonly translateService: TranslateService,
              private readonly httpClient: HttpClient,
              private readonly snackBarService: SnackBarService,
              collaboratorHelperService: CollaboratorHelperService,
              applicationInfoService: ApplicationInfoService,
              sessionService: SessionService) {
    this.selectedLanguage = this.availableLanguages.find(lang => lang.code === this.translateService.currentLang) || this.availableLanguages[0];
    const currentUserUrl = sessionService.session().user?._links.getLink('self')?.href;
    if (!collaboratorHelperService.isCollaboratorView && currentUserUrl) {
      const context = new HttpContext().set(SKIP_EXCEPTION_HANDLING, true);
      this.httpClient.get<UserHal>(currentUserUrl, {context}).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: response => {
          const currentUser = new UserHal(response);
          this.notificationPreferencesUrl = currentUser._links.getLink('notificationPreferences')?.href;
          if (this.notificationPreferencesUrl) {
            this.loadNotificationPreferences(this.notificationPreferencesUrl);
          }
        },
        error: () => {
          this.snackBarService.open('settings.notifications.loadError', 3, 'error');
        }
      });
    }
    if (!collaboratorHelperService.isCollaboratorView) {
      applicationInfoService.getApplicationInfo().pipe(
        takeUntil(this.destroy$) // Ensure to unsubscribe when the component is destroyed
      ).subscribe(applicationInfo => {
        this.applicationInfo = applicationInfo;
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeLanguage(language: { code: LanguageEnum; label: string }) {
    this.translatorService.switchLanguage(language.code);
  }

  updateNotificationPreference(preference: UserNotificationPreference, enabled: boolean) {
    if (!this.notificationPreferencesUrl) {
      return;
    }
    const previousValue = preference.enabled;
    preference.enabled = enabled;
    preference.updating = true;
    this.httpClient.put<UserNotificationPreference>(
      `${this.notificationPreferencesUrl}/${preference.type}`,
      {enabled},
      {context: new HttpContext().set(SKIP_EXCEPTION_HANDLING, true)}
    ).subscribe({
      next: updatedPreference => {
        preference.enabled = updatedPreference.enabled;
        preference.updating = false;
      },
      error: () => {
        preference.enabled = previousValue;
        preference.updating = false;
        this.snackBarService.open('settings.notifications.updateError', 3, 'error');
      }
    });
  }

  private loadNotificationPreferences(url: string) {
    const context = new HttpContext().set(SKIP_EXCEPTION_HANDLING, true);
    this.httpClient.get<UserNotificationPreference[]>(url, {context}).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: preferences => {
        this.notificationPreferences = preferences;
      },
      error: () => {
        this.notificationPreferences = [];
        this.snackBarService.open('settings.notifications.loadError', 3, 'error');
      }
    });
  }
}
